import { journeyToken, JourneyStages } from '@cognitoforms/api/services/journey-context';
import type { StageStart } from '@cognitoforms/api/services/journey-context';

/**
 * The surfaces allowed to name themselves in the signup page's `source` parameter. It is a query parameter,
 * so the allowlist is what keeps a reporting dimension from becoming unbounded free text.
 */
const signupOrigins: readonly string[] = [
	'HomePageCTA',
	'PricingPageCTA',
	'TemplateGalleryCTA',
	'NavBar',
	'JotformHandoff'
];

/**
 * What arriving at the signup page means, from the parameters the arrival carries. `Variant` is what the
 * person was trying to do and stays a small closed set; where they came from goes to `entryPoint`.
 */
export function resolveCreateOrganizationStart(source?: unknown, returnUrl?: unknown): StageStart {
	if (typeof returnUrl === 'string' && returnUrl.toLowerCase().includes('api-connection'))
		return { stage: JourneyStages.CreateOrganization, variant: 'IntegrationConnect' };

	const origin = typeof source === 'string' && signupOrigins.includes(source) ? source : undefined;

	if (origin === 'JotformHandoff')
		return { stage: JourneyStages.CreateOrganization, variant: 'Jotform', entryPoint: origin };

	const start: StageStart = { stage: JourneyStages.CreateOrganization, variant: 'Website' };
	if (origin)
		start.entryPoint = origin;
	return start;
}

/**
 * What each value of the plan page's `source` parameter means as an upgrade intent. The keys are the values
 * actually in use; anything else is treated as no source at all.
 */
const upgradeIntents: Record<string, string> = {
	unavailfeat: 'LimitReached',
	feature: 'FeatureLocked',
	'tryitnow-feature': 'FeatureLocked',
	'upgrade-permissions': 'FeatureLocked',
	'upgrade-folders': 'FeatureLocked',
	orgsettings: 'BillingSettings',
	'upgrade-now': 'UpgradeCTA'
};

const MAX_UPGRADE_DETAIL_LENGTH = 48;

/**
 * Reduces the `details` parameter — the feature code that was gated — to something safe to group by. There
 * are too many feature codes to allowlist, so it is bounded by shape instead.
 */
function upgradeDetail(details: unknown): string | undefined {
	if (typeof details !== 'string')
		return undefined;
	return journeyToken(details, MAX_UPGRADE_DETAIL_LENGTH) || undefined;
}

/**
 * What reaching the plan page means, from the parameters the arrival carries. `Variant` says what kind of
 * pressure brought them and stays a small closed set; the raw `source` goes to `entryPoint` and the feature
 * code to `detail`.
 */
export function resolveUpgradeStart(source?: unknown, details?: unknown, isSignUp = false): StageStart {
	const start: StageStart = {
		stage: JourneyStages.ConvertToPaid,
		variant: isSignUp ? 'Signup' : 'Direct'
	};

	const detail = upgradeDetail(details);
	if (detail)
		start.detail = detail;

	if (typeof source !== 'string' || !Object.prototype.hasOwnProperty.call(upgradeIntents, source))
		return start;

	if (!isSignUp)
		start.variant = upgradeIntents[source];
	start.entryPoint = source;
	return start;
}
